import { DetailsListLayoutMode, IColumn, IGroup, IObjectWithKey, SelectionMode } from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { onRenderColumnCurrency } from "components/controls/Grid";
import { bcbs_config_attributeid_BCCAdmin, bcbs_config_attributeid_BillGroup, bcbs_config_attributeid_CoverageList, bcbs_config_attributeid_FeeType, pepmFeeType, pepmPerTierFeeType } from "global/Application";
import { groupByWithAggregate, IReturn, TGroupByAggregate, TGroupByAggregateMethod } from "global/Utility/TSFunctions";
import * as IXrmOptionSet from "model/AttributeOptionSets.type";
import {
    bcbs_bccadmin as IXrmTypes_bcbs_bccadmin,
    bcbs_config_attribute as IXrmTypes_bcbs_config_attribute
} from "model/CRM";
import { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
    IBooleanValue as FAM_IBooleanValue,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    IStringValue as FAM_IStringValue
} from "../../model/FlexibleAttributeModel";
import { StickyDetailsList } from "../../components/layout/StickyDetailList";
import { $att_$fees_and_premiums_$bcbs_attributeset_fee_id, $att_$fees_and_premiums_$bcbs_concessionamount_id } from "model/ConfigAttributeConstants";

interface IProps extends TComponentConnectedProps {
}


export interface IFeeSummaryRowItem extends IObjectWithKey {
    feesAndPremiumName: string;
    category?: string;
    'Family Rate': string,
    "Employee Only Rate": string,
    "Employee + Child(ren) Rate": string,
    "Employee + Spouse Rate": string,
    "Employee + 1 (Child) Rate": string,
    "Two-Person Rate": string,
    "Employee + 2 Dependents Rate": string,
    "Concession Amount": string,
    "billGroup": boolean;
    feeTypeName: string;
    stopLossTypeName: string;
}


export function ExtractFees(config: FAM_IGroupConfigurationConfig, json: FAM_IGroupConfigurationData) {
    let feeSummary: IFeeSummaryRowItem[] = [];

    let stopLoss: IFeeSummaryRowItem[] = [];

    // fee config:
    const attributeFee = config.cache.map_bcbs_config_attribute[$att_$fees_and_premiums_$bcbs_attributeset_fee_id];

    json.feesAndPremiums?.forEach(eachFeePremium => {

        if (eachFeePremium.deactivated)
            return;

        // defaults
        eachFeePremium.attributeSets.forEach(eachAttribute => {
            let bccAdmin: IXrmTypes_bcbs_bccadmin | null = null;

            const item: IFeeSummaryRowItem = {
                key: eachAttribute.id,
                billGroup: false,
                feesAndPremiumName: eachFeePremium.name
            } as IFeeSummaryRowItem;

            // Extract
            // ----------------------
            // Pick only FEE attribute set
            if (eachAttribute.attributeSetRef.id !== attributeFee._bcbs_config_masterattributeid_value)
                return;

            if (eachAttribute.deactivated)
                return;

            // Transform
            // ----------------------
            eachAttribute.fields.forEach(eachField => {
                const configAttr = config.cache.map_bcbs_config_attribute[eachField.configRef.id];

                if (configAttr.bcbs_config_attributeid === bcbs_config_attributeid_BCCAdmin) {

                    // is this field a BCC Admin field?
                    // its used as a criteria to aggregate on for calculations

                    const value = eachField?.values?.[0]?.value as FAM_IEntityReferenceValue;

                    if (value) {
                        // lets find the category selected in bcc admin
                        bccAdmin = config.cache.map_bcbs_bccadmin[value.id];


                        if (bccAdmin && bccAdmin._bcbs_categoryid_value) {
                            const category = config.cache.map_bcbs_config_category[bccAdmin._bcbs_categoryid_value!];
                            if (category)
                                item.category = category.bcbs_name;
                        } else {
                            console.warn("BCC admin is missing a category identifier.", bccAdmin);
                        }
                    }

                } else if (configAttr.bcbs_config_attributeid === bcbs_config_attributeid_BillGroup) {

                    // is this field "Bill Group" ?
                    // its used as a criteria to include or not

                    const value = eachField?.values?.[0]?.value as FAM_IBooleanValue;
                    if (value)
                        item.billGroup = value.value;

                } else if (configAttr.bcbs_config_attributeid === bcbs_config_attributeid_FeeType) {

                    // is this field "Fee Type" ?
                    // its used as a criteria to include or not

                    const value = eachField?.values?.[0]?.value as FAM_IEntityReferenceValue;
                    if (value)
                        item.feeTypeName = value.name;

                } else if (bcbs_config_attributeid_CoverageList.includes(configAttr.bcbs_config_attributeid!)) {

                    // any of or expected fields 
                    const property = configAttr.bcbs_name as (keyof typeof item);
                    const value = eachField?.values?.[0]?.value as FAM_IStringValue;

                    if (value)
                        (item[property] as string) = value.value;
                }
            });

            // Load
            // ----------------------
            feeSummary.push(item);

            if (bccAdmin) {

                // TODO typescript appears to have bug, assumes eachAttributeBccAdmin:never; get assigned in field aggregate
                const _bccAdmin = bccAdmin as IXrmTypes_bcbs_bccadmin;

                if (_bccAdmin.bcbs_stoplosstypecode === IXrmOptionSet.bcbs_bccadmin.bcbs_stoplosstypecode["Aggregate(808900000)"].value) {
                    item.stopLossTypeName = 'Aggregate'
                    stopLoss.push(item);
                }

                if (_bccAdmin.bcbs_stoplosstypecode === IXrmOptionSet.bcbs_bccadmin.bcbs_stoplosstypecode["Specific(808900001)"].value) {
                    item.stopLossTypeName = 'Specific'
                    stopLoss.push(item);
                }

            }

        })

    });

    return {
        feeSummary,
        stopLoss
    }
}

export const aggSum: TGroupByAggregateMethod<IFeeSummaryRowItem> = (prev, current, key, row): string => {
    const prevInt = prev ? parseFloat(prev as string) : 0;
    const currentInt = current ? parseFloat(current as string) : 0;

    // we are excluding 'Per Employee Per Month (PEPM)' from aggregate
    if (row.feeTypeName !== pepmFeeType.name && row.feeTypeName !== pepmPerTierFeeType.name)
        return (prevInt).toString();

    // if bill group is Yes?
    if (row.billGroup) {
        return (prevInt + currentInt).toString();
    }
    // 'Concession Amount' ignores bill group in the condition
    if (key === 'Concession Amount') {
        return (prevInt + currentInt).toString();
    }

    return (prevInt).toString();
};

// const aggNoOp = (prev: string, current: string, row: any): string => {
//     return '';
// };

export const aggLastString: TGroupByAggregateMethod<IFeeSummaryRowItem> = (prev, current, key, row) => {
    return current;
};

export const aggPredicate: TGroupByAggregate<IFeeSummaryRowItem> = {
    "feesAndPremiumName": aggLastString,
    "Employee Only Rate": aggSum,
    'Family Rate': aggSum,
    "Employee + Child(ren) Rate": aggSum,
    "Employee + Spouse Rate": aggSum,
    "Employee + 1 (Child) Rate": aggSum,
    "Two-Person Rate": aggSum,
    "Employee + 2 Dependents Rate": aggSum,
    "Concession Amount": aggSum,
}

interface ICustomColumn<T> extends IColumn {
    data?: T
}

type TColumn = ICustomColumn<IXrmTypes_bcbs_config_attribute>

const MonthlyRecurringFeesAgg: React.FC<IProps> = (props) => {

    const feeSummaryColumns = useMemo(() => {
        const feeSummaryColumns: TColumn[] = [];

        // columns.push({
        //     key: 'category',
        //     name: 'Category',
        //     fieldName: 'category',
        //     minWidth: 100,
        //     maxWidth: 200,
        //     isResizable: true,
        // });

        feeSummaryColumns.push({
            key: 'feesAndPremiumName',
            name: 'Fees And Premium',
            fieldName: 'feesAndPremiumName',
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
        });

        bcbs_config_attributeid_CoverageList.forEach(e => {
            const configAttribute = props.state.data.config.cache.map_bcbs_config_attribute[e];
            feeSummaryColumns.push({
                key: configAttribute.bcbs_config_attributeid!,
                name: configAttribute.bcbs_name!,
                fieldName: configAttribute.bcbs_name,
                minWidth: configAttribute.bcbs_viewcolumnwidth ?? 100,
                maxWidth: 160,
                isResizable: true,
                onRender: onRenderColumnCurrency
            })
        });

        return feeSummaryColumns;
    }, []);

    const stopLossColumns = useMemo(() => {
        const stopLossColumns: TColumn[] = [];

        // columns.push({
        //     key: 'category',
        //     name: 'Category',
        //     fieldName: 'category',
        //     minWidth: 100,
        //     maxWidth: 200,
        //     isResizable: true,
        // });

        stopLossColumns.push({
            key: 'feesAndPremiumName',
            name: 'Fees And Premium',
            fieldName: 'feesAndPremiumName',
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
        });

        bcbs_config_attributeid_CoverageList.forEach(e => {
            const configAttribute = props.state.data.config.cache.map_bcbs_config_attribute[e];
            stopLossColumns.push({
                key: configAttribute.bcbs_config_attributeid!,
                name: configAttribute.bcbs_name!,
                fieldName: configAttribute.bcbs_name,
                minWidth: configAttribute.bcbs_viewcolumnwidth ?? 100,
                maxWidth: 160,
                isResizable: true,
                onRender: onRenderColumnCurrency,
                data: configAttribute,
            })
        })

        const concessionAmountIndex = stopLossColumns.findIndex(q => q.data?.bcbs_config_attributeid === $att_$fees_and_premiums_$bcbs_concessionamount_id)
        stopLossColumns.splice(concessionAmountIndex, 1);

        return stopLossColumns;
    }, []);


    const dataSource = useMemo(() => {



        function aggregateToGridGroupByRef<T>(groupKeyValues: Record<string, IReturn<T>>, depth: number, parentGroupByRef: IGroup, itemsByRef: T[], maxDepth = -1) {
            // lets extract
            const continueGrouping = maxDepth === -1 || depth <= maxDepth;

            for (const [key, value] of Object.entries(groupKeyValues)) {

                if (value.grouping) {
                    const startIndex = itemsByRef.length;

                    const scopeGroup = {
                        key: key,
                        name: key,
                        startIndex: startIndex,
                        count: 0,
                        level: depth - 1,
                        isCollapsed: depth > 0 ? true : false
                    } as IGroup;

                    if (continueGrouping) {
                        if (!parentGroupByRef.children)
                            parentGroupByRef.children = [];

                        parentGroupByRef.children.push(scopeGroup);
                    }

                    aggregateToGridGroupByRef(value.grouping, depth + 1, scopeGroup, itemsByRef, maxDepth);
                    parentGroupByRef.count = parentGroupByRef.count + scopeGroup.count

                } else {
                    const startIndex = itemsByRef.length;
                    let count = 0;

                    // value.rows?.forEach(r => newItems.push(r));
                    if (value.rowsAggregate) {
                        itemsByRef.push(value.rowsAggregate);
                        count = 1;
                    }
                    else {
                        value.rows?.forEach(r => itemsByRef.push(r));
                        count = value.rows?.length ?? count;
                    }

                    const scopeGroup = {
                        key: key,
                        name: key,
                        startIndex: startIndex,
                        // count: value.rows?.length ?? 0,
                        count: count,
                        level: depth,
                        isCollapsed: depth > 0 ? true : false
                    } as IGroup;

                    if (continueGrouping) {

                        if (!parentGroupByRef.children)
                            parentGroupByRef.children = [];

                        parentGroupByRef.children.push(scopeGroup);
                    }

                    parentGroupByRef.count = parentGroupByRef.count + scopeGroup.count

                }
            }
        }


        const { feeSummary: feeSummaryRows, stopLoss: stopLossRows } = ExtractFees(props.state.data.config, props.state.data.json);
        const feeSummaryGroupBy = groupByWithAggregate(feeSummaryRows, ['category', 'feesAndPremiumName'], aggPredicate);
        const feeSummaryGroupContainer: IGroup = {} as IGroup;
        const feeSummaryItems: any[] = [];
        aggregateToGridGroupByRef(
            { root: feeSummaryGroupBy },
            0,
            feeSummaryGroupContainer,
            feeSummaryItems
        );
        const feeSummaryGroups = feeSummaryGroupContainer.children?.[0].children!;
        // I dont want the inner aggregates, only first level after root
        if (feeSummaryGroups)
            feeSummaryGroups.forEach(q => q.children = []);


        const stopLossGroupBy = groupByWithAggregate(stopLossRows, ['stopLossTypeName', 'category', 'feesAndPremiumName'], aggPredicate);
        const stopLossGroupRoot: IGroup = {} as IGroup;
        const stopLossItems: any[] = [];
        aggregateToGridGroupByRef(
            { root: stopLossGroupBy },
            0,
            stopLossGroupRoot,
            stopLossItems,
            2
        );
        const stopLossGroups = stopLossGroupRoot.children?.[0].children!;

        return {
            feeSummary: {
                groups: feeSummaryGroups,
                items: feeSummaryItems
            },
            stopLoss: {
                groups: stopLossGroups,
                items: stopLossItems
            },

        };


    }, [feeSummaryColumns]);


    return <>
        <CustomPanel title={'Fees Summary'}
            canCollapse={true}
            defaultCollapsed={false}
            storageId={'monthlyRecurringFees'}
            mountWhenExpanded={true}
        >
            <StickyDetailsList
                items={dataSource.feeSummary.items}
                groups={dataSource.feeSummary.groups}
                columns={feeSummaryColumns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                groupProps={{
                    showEmptyGroups: true,
                }}
            />
        </CustomPanel>

        <CustomPanel title={'Stop Loss Summary'}
            canCollapse={true}
            defaultCollapsed={false}
            storageId={'stopLossSummary'}
            mountWhenExpanded={true}
        >
            <StickyDetailsList
                items={dataSource.stopLoss.items}
                groups={dataSource.stopLoss.groups}
                columns={stopLossColumns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                groupProps={{
                    showEmptyGroups: true,
                }}
            />
        </CustomPanel>
    </>
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(MonthlyRecurringFeesAgg);

